import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import API_URL from '../../config/api';
import { showToast } from '../../utils/toast';
import { portalInviteButtonLabel, resolveTenantPortalStatus } from '../../utils/tenantPortalStatus';
import {
  UK, fmt, ordinal, expiryBadge, depositBadge, s13StatusLabel, isLeaseEnded, readyToRenew,
} from './leaseFormShared';

export default function LeaseViewPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const fileRef = useRef<HTMLInputElement>(null);

  const [lease, setLease] = useState<any | null>(null);
  const [documents, setDocuments] = useState<any[]>([]);
  const [increases, setIncreases] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [showS13, setShowS13] = useState(searchParams.get('s13') === '1');
  const [s13Form, setS13Form] = useState({ proposed_rent: '', effective_date: '', notice_date: '', notes: '' });
  const [savingS13, setSavingS13] = useState(false);
  const [servingId, setServingId] = useState<number | null>(null);
  const [applyingId, setApplyingId] = useState<number | null>(null);
  const [linkedTenantId, setLinkedTenantId] = useState<number | null>(null);
  const [linkedPortalStatus, setLinkedPortalStatus] = useState<string>('not_invited');

  const load = useCallback(async () => {
    if (!token || !id) return;
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/landlord/leases/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Not found');
      setLease(d.lease);
      setDocuments(d.documents || []);
      setIncreases(d.rent_increases || []);
      const l = d.lease;
      const eff = new Date();
      eff.setMonth(eff.getMonth() + 2);
      const notice = new Date(eff);
      notice.setMonth(notice.getMonth() - 1);
      setS13Form({
        proposed_rent: String(Math.round(Number(l.monthly_rent || 0) * 1.05)),
        effective_date: eff.toISOString().split('T')[0],
        notice_date: notice.toISOString().split('T')[0],
        notes: '',
      });
    } catch (e: any) {
      showToast(e.message || 'Failed to load');
      navigate('/landlord/leases');
    } finally { setLoading(false); }
  }, [token, id, navigate]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!lease || !token) return;
    if (lease.tenant_id) {
      setLinkedTenantId(Number(lease.tenant_id));
      fetch(`${API_URL}/landlord/tenants/${lease.tenant_id}`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : null)
        .then(d => { if (d?.tenant) setLinkedPortalStatus(d.tenant.portal_status || resolveTenantPortalStatus(d.tenant)); })
        .catch(() => undefined);
      return;
    }
    fetch(`${API_URL}/landlord/tenants`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => {
        const match = (d.tenants || []).find((t: any) =>
          String(t.full_name || '').toLowerCase() === String(lease.tenant_name || '').toLowerCase()
        );
        if (match?.id) {
          setLinkedTenantId(match.id);
          setLinkedPortalStatus(match.portal_status || resolveTenantPortalStatus(match));
        }
      })
      .catch(() => {});
  }, [lease, token]);

  const generatePdf = async () => {
    if (!lease) return;
    setGenerating(true);
    try {
      const r = await fetch(`${API_URL}/landlord/leases/${lease.id}/agreement`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) { const d = await r.json(); showToast(d.error || 'Failed'); setGenerating(false); return; }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `tenancy-${lease.property_address.replace(/[^a-zA-Z0-9]/g, '-').slice(0, 40)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      showToast('PDF saved — also available in tenant portal');
      load();
    } catch { showToast('Failed to generate PDF'); }
    setGenerating(false);
  };

  const emailAgreement = async () => {
    if (!lease) return;
    if (!window.confirm(`Email tenancy agreement to tenant?`)) return;
    setEmailing(true);
    try {
      const r = await fetch(`${API_URL}/landlord/leases/${lease.id}/agreement/email`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      showToast(r.ok ? `Sent to ${d.sent_to}` : d.error || 'Failed to send');
      if (r.ok) load();
    } catch { showToast('Network error'); }
    setEmailing(false);
  };

  const invitePortal = async () => {
    if (!linkedTenantId) { showToast('Link a tenant record first'); return; }
    setInviting(true);
    try {
      const r = await fetch(`${API_URL}/landlord/tenants/${linkedTenantId}/invite-portal`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      showToast(r.ok ? `Portal invite sent` : d.error || 'Failed');
      if (r.ok) setLinkedPortalStatus(d.portal_status || 'invited');
    } catch { showToast('Network error'); }
    setInviting(false);
  };

  const uploadDocs = async (files: FileList | null) => {
    if (!files?.length || !lease) return;
    setUploading(true);
    const fd = new FormData();
    Array.from(files).forEach(f => fd.append('documents', f));
    const r = await fetch(`${API_URL}/landlord/leases/${lease.id}/documents`, {
      method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
    });
    const d = await r.json();
    if (!r.ok) showToast(d.error || 'Upload failed');
    else { showToast(`${(d.documents || []).length} file(s) uploaded`); load(); }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const deleteDoc = async (docId: number) => {
    if (!lease || !window.confirm('Delete this document?')) return;
    await fetch(`${API_URL}/landlord/leases/${lease.id}/documents/${docId}`, {
      method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
    });
    showToast('Document removed');
    load();
  };

  const saveS13 = async () => {
    if (!lease) return;
    setSavingS13(true);
    try {
      const r = await fetch(`${API_URL}/landlord/rent-increases`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ lease_id: lease.id, ...s13Form, proposed_rent: Number(s13Form.proposed_rent) }),
      });
      const d = await r.json();
      if (!r.ok) showToast(d.error || 'Failed');
      else { showToast('Section 13 drafted — serve notice when ready'); load(); }
    } catch { showToast('Network error'); }
    setSavingS13(false);
  };

  const serveNotice = async (incId: number) => {
    if (!window.confirm('Email Section 13 notice to tenant?')) return;
    setServingId(incId);
    try {
      const r = await fetch(`${API_URL}/landlord/rent-increases/${incId}/serve-notice`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json();
      showToast(r.ok ? 'Notice emailed to tenant' : d.error || 'Failed');
      if (r.ok) load();
    } catch { showToast('Network error'); }
    setServingId(null);
  };

  const applyIncrease = async (incId: number) => {
    if (!window.confirm('Apply this rent increase to the lease and rent tracker now?')) return;
    setApplyingId(incId);
    try {
      const r = await fetch(`${API_URL}/landlord/rent-increases/${incId}/apply`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json();
      showToast(r.ok ? 'Rent increase applied' : d.error || 'Failed');
      if (r.ok) load();
    } catch { showToast('Network error'); }
    setApplyingId(null);
  };

  if (loading || !lease) {
    return <div style={{ padding: 80, textAlign: 'center', color: '#9ca3af' }}>Loading…</div>;
  }

  const badge = expiryBadge(lease);
  const dep = depositBadge(lease);
  const ended = isLeaseEnded(lease);

  return (
    <div style={{ minHeight: '100vh', background: '#eef2f6', fontFamily: "'DM Sans', system-ui, sans-serif", paddingBottom: 40 }}>
      <div style={{ background: '#fff', borderBottom: '1px solid #e5e7eb', padding: '16px 24px' }}>
        <button type="button" onClick={() => navigate('/landlord/leases')} style={{ background: 'none', border: 'none', color: '#374151', fontSize: 13, fontWeight: 600, cursor: 'pointer', marginBottom: 8, display: 'block' }}>← Back to Leases</button>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800 }}>{lease.tenant_name}</h1>
            <p style={{ margin: '6px 0 0', color: '#6b7280', fontSize: 14 }}>{lease.property_address}{lease.room_number ? ` — ${lease.room_number}` : ''}</p>
            <p style={{ margin: '8px 0 0', fontSize: 13, color: UK.blue, fontWeight: 700 }}>
              Legal landlord: {lease.legal_landlord || '—'}
            </p>
            <div style={{ marginTop: 10, display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 12 }}>
              {lease.property_id && (
                <button type="button" onClick={() => navigate(`/landlord/properties/${lease.property_id}`)} style={{ background: 'none', border: 'none', color: '#16a34a', fontWeight: 700, cursor: 'pointer', padding: 0, textDecoration: 'underline' }}>
                  View property →
                </button>
              )}
              {linkedTenantId && (
                <button type="button" onClick={() => navigate(`/landlord/tenants/${linkedTenantId}`)} style={{ background: 'none', border: 'none', color: '#16a34a', fontWeight: 700, cursor: 'pointer', padding: 0, textDecoration: 'underline' }}>
                  View tenant →
                </button>
              )}
            </div>
          </div>
          <span style={{ background: badge.bg, color: badge.color, padding: '6px 14px', borderRadius: 8, fontWeight: 700, fontSize: 13 }}>{badge.label}</span>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
          <button type="button" onClick={() => navigate(`/landlord/leases/${id}/edit`)} style={{ padding: '8px 16px', borderRadius: 8, border: `1.5px solid ${UK.blue}`, background: '#fff', color: UK.blue, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>✏️ Edit</button>
          <button type="button" onClick={generatePdf} disabled={generating} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: UK.blue, color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>{generating ? '…' : '📄 PDF'}</button>
          <button type="button" onClick={emailAgreement} disabled={emailing} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: '#16a34a', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>{emailing ? '…' : '📧 Send'}</button>
          {linkedTenantId && (
            <button type="button" onClick={invitePortal} disabled={inviting || linkedPortalStatus === 'active'} style={{ padding: '8px 16px', borderRadius: 8, border: '1.5px solid #ec4899', background: '#fdf2f8', color: '#9d174d', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>{portalInviteButtonLabel(linkedPortalStatus as any, inviting)}</button>
          )}
          {!ended && (
            <>
              <button type="button" onClick={() => setShowS13(v => !v)} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: UK.gold, color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>S13</button>
              <button type="button" onClick={() => navigate(`/landlord/leases?end=${id}`)} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: UK.red, color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>End</button>
            </>
          )}
          {readyToRenew(lease) && (
            <button type="button" onClick={() => navigate(`/landlord/leases/new?renew_from=${lease.id}`)} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: '#7c3aed', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>Renew</button>
          )}
        </div>
      </div>

      <div style={{ maxWidth: 920, margin: '24px auto', padding: '0 24px' }}>
        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: '12px 16px', marginBottom: 16, fontSize: 13, color: '#92400e', lineHeight: 1.5 }}>
          <strong>Tenancy agreement summary</strong> — PDF and email outputs summarise key terms for your records. They are not a substitute for a solicitor-drafted Assured Shorthold Tenancy (AST).
        </div>
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', padding: 22, marginBottom: 16 }}>
          <h2 style={{ margin: '0 0 16px', fontSize: 11, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#374151' }}>Tenancy Details</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 16, fontSize: 14 }}>
            <div><div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 700 }}>LEGAL LANDLORD</div><div style={{ fontWeight: 700 }}>{lease.legal_landlord || '—'}</div></div>
            <div><div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 700 }}>TYPE</div><div style={{ fontWeight: 700 }}>{lease.tenancy_type || 'AST'}</div></div>
            <div><div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 700 }}>TERM</div><div style={{ fontWeight: 700 }}>{fmt(lease.start_date)} → {fmt(lease.end_date)}</div></div>
            <div><div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 700 }}>RENT</div><div style={{ fontWeight: 800, color: UK.blue }}>£{Number(lease.monthly_rent).toLocaleString()}/mo</div></div>
            <div><div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 700 }}>DUE DAY</div><div style={{ fontWeight: 700 }}>{ordinal(Number(lease.payment_day || 1))} of month</div></div>
            <div><div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 700 }}>DEPOSIT</div><div style={{ fontWeight: 700 }}>£{Number(lease.deposit || 0).toLocaleString()}</div></div>
            {dep && <div><div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 700 }}>PROTECTION</div><span style={{ background: dep.bg, color: dep.color, padding: '2px 8px', borderRadius: 6, fontSize: 12, fontWeight: 700 }}>{dep.label}</span></div>}
            {lease.renewal_of_lease_id && <div><div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 700 }}>RENEWAL OF</div><div style={{ fontWeight: 700, color: '#7c3aed' }}>Lease #{lease.renewal_of_lease_id}</div></div>}
            {lease.agreement_signed && <div><div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 700 }}>SIGNED</div><div style={{ fontWeight: 700, color: '#16a34a' }}>Yes</div></div>}
          </div>
          {!lease.legal_landlord_from_property && lease.property_id && (
            <p style={{ margin: '14px 0 0', fontSize: 12, color: '#92400e', background: '#fef3c7', padding: '10px 12px', borderRadius: 8 }}>
              Legal landlord not set on the property — AST PDF uses your business name.{' '}
              <button type="button" onClick={() => navigate(`/landlord/properties/${lease.property_id}/edit`)} style={{ background: 'none', border: 'none', color: UK.blue, fontWeight: 700, cursor: 'pointer', padding: 0, textDecoration: 'underline' }}>
                Set on property →
              </button>
            </p>
          )}
          {lease.notes && <p style={{ marginTop: 16, fontSize: 13, color: '#64748b', borderTop: '1px solid #f1f5f9', paddingTop: 12 }}>{lease.notes}</p>}
        </div>

        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', padding: 22, marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <h2 style={{ margin: 0, fontSize: 11, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#374151' }}>Documents ({documents.length})</h2>
            <div>
              <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png" multiple style={{ display: 'none' }} onChange={e => uploadDocs(e.target.files)} />
              <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} style={{ padding: '8px 14px', borderRadius: 8, border: '1px dashed #16a34a', background: '#f0fdf4', color: '#16a34a', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
                {uploading ? 'Uploading…' : '+ Upload files'}
              </button>
            </div>
          </div>
          {documents.length === 0 ? (
            <p style={{ color: '#9ca3af', fontSize: 13, margin: 0 }}>No documents yet — upload agreements, inventory, deposit certificates, etc.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {documents.map((doc: any) => (
                <div key={doc.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                  <span style={{ fontSize: 13, fontWeight: 600, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.file_name}</span>
                  <span style={{ fontSize: 11, color: '#94a3b8' }}>{doc.doc_type || 'other'}</span>
                  <a href={doc.file_url} target="_blank" rel="noopener noreferrer" style={{ padding: '5px 12px', background: UK.blue, color: '#fff', borderRadius: 6, fontSize: 12, fontWeight: 700, textDecoration: 'none' }}>Download</a>
                  <button type="button" onClick={() => deleteDoc(doc.id)} style={{ padding: '5px 10px', background: 'none', border: '1px solid #fca5a5', color: UK.red, borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>Delete</button>
                </div>
              ))}
            </div>
          )}
          {lease.document_url && !documents.some((d: any) => d.file_url === lease.document_url) && (
            <div style={{ marginTop: 10 }}>
              <a href={lease.document_url} target="_blank" rel="noopener noreferrer" style={{ color: '#0d9488', fontSize: 13, fontWeight: 700 }}>Legacy document</a>
            </div>
          )}
        </div>

        {(showS13 || increases.length > 0) && !ended && (
          <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', padding: 22 }}>
            <h2 style={{ margin: '0 0 8px', fontSize: 11, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#374151' }}>Section 13 Rent Increase</h2>
            <p style={{ margin: '0 0 16px', fontSize: 12, color: '#64748b' }}>
              <strong>Draft</strong> = saved, not sent. <strong>Serve notice</strong> = emailed to tenant. <strong>Effective</strong> = rent applied on effective date (or apply manually).
            </p>
            {showS13 && (
              <div style={{ background: '#f8fafc', borderRadius: 10, padding: 16, marginBottom: 16 }}>
                <div style={{ fontSize: 13, marginBottom: 12 }}>Current rent: <strong>£{Number(lease.monthly_rent).toLocaleString()}/mo</strong></div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12 }}>
                  <div><label style={{ fontSize: 12, fontWeight: 600 }}>Proposed Rent (£)</label><input type="number" value={s13Form.proposed_rent} onChange={e => setS13Form(f => ({ ...f, proposed_rent: e.target.value }))} style={{ width: '100%', padding: 8, borderRadius: 8, border: '1px solid #d1d5db', marginTop: 4 }} /></div>
                  <div><label style={{ fontSize: 12, fontWeight: 600 }}>Effective Date</label><input type="date" value={s13Form.effective_date} onChange={e => setS13Form(f => ({ ...f, effective_date: e.target.value }))} style={{ width: '100%', padding: 8, borderRadius: 8, border: '1px solid #d1d5db', marginTop: 4 }} /></div>
                  <div><label style={{ fontSize: 12, fontWeight: 600 }}>Notice Date</label><input type="date" value={s13Form.notice_date} onChange={e => setS13Form(f => ({ ...f, notice_date: e.target.value }))} style={{ width: '100%', padding: 8, borderRadius: 8, border: '1px solid #d1d5db', marginTop: 4 }} /></div>
                </div>
                <button type="button" onClick={saveS13} disabled={savingS13} style={{ marginTop: 12, padding: '9px 18px', background: UK.gold, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>{savingS13 ? 'Saving…' : 'Save Draft'}</button>
              </div>
            )}
            {increases.map((inc: any) => {
              const st = s13StatusLabel(inc);
              return (
                <div key={inc.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 8, marginBottom: 8, flexWrap: 'wrap', fontSize: 13 }}>
                  <span>£{Number(inc.current_rent).toLocaleString()} → <strong style={{ color: UK.blue }}>£{Number(inc.proposed_rent).toLocaleString()}</strong></span>
                  <span style={{ color: '#64748b' }}>from {fmt(inc.effective_date)}</span>
                  <span style={{ background: st.bg, color: st.color, padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700 }}>{st.label}</span>
                  {inc.status === 'draft' && !inc.notice_served && (
                    <button type="button" onClick={() => serveNotice(inc.id)} disabled={servingId === inc.id} style={{ marginLeft: 'auto', padding: '6px 14px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
                      {servingId === inc.id ? 'Sending…' : 'Serve Notice'}
                    </button>
                  )}
                  {inc.notice_served && inc.status !== 'effective' && (
                    <button type="button" onClick={() => applyIncrease(inc.id)} disabled={applyingId === inc.id} style={{ marginLeft: 'auto', padding: '6px 14px', background: UK.blue, color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
                      {applyingId === inc.id ? 'Applying…' : 'Apply Now'}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
